/**
 * Valuator's application shell, mounted once the viewer is signed in (App.tsx). Valuator is its
 * own package and shares no source with v1 - its own stores, API prefix and model shape.
 *
 * A project list and the sheet workspace. Models can be created empty,
 * imported from a workbook in the template's layout, or built from a
 * statutory accounts PDF.
 *
 * The PDF import reports its warnings here rather than inside the model: they
 * are about what could NOT be read, and a user who has already started editing
 * has lost the chance to check them against the filing.
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Button, buttonClass } from './ui/Button';
import { Notice, Panel, ProjectsPage } from './ui/ProjectsPage';
import { FolderedProjects } from './ui/ProjectFolders';
import { openSampleAt, useSampleCatalogue } from '../../store/samples';
import PdfImportProgress from './PdfImportProgress';
import CompaniesHouseImport from './CompaniesHouseImport';
import { TypedConfirm } from './ui/TypedConfirm';
import { ReadAfresh } from './ui/ReadAfresh';
import DisciplineChooser from './DisciplineChooser';
import ValuationProjectList from './valuation/ValuationProjectList';
import ValuationWorkspace from './valuation/ValuationWorkspace';
import { useValuationStore } from '../../store/valuation-projects';
import {
  disciplineFor,
  readDiscipline,
  writeDiscipline,
  type Discipline,
  type DisciplineId,
} from './disciplines';
import { useFinancialModelV2Store } from '../../store/financial-model-v2';
import type { ModelMetadataV2 } from '../../types/financial-model-v2';
import { V2Workspace } from './V2Workspace';
import { MProjectList } from './m/MProjectList';
import { MWorkspace } from './m/MWorkspace';
import { useMAccountingStore } from '../../store/m-accounting';
import { DiligenceProjectList } from './diligence/DiligenceProjectList';
import { DiligenceWorkspace } from './diligence/DiligenceWorkspace';
import { useDiligenceStore } from '../../store/diligence-projects';

// Books, the fifth tool, in its own chunk: its screens and styles load when it is first opened.
const BooksTool = lazy(() => import('./books/BooksTool'));

/**
 * One model in the list, with the controls for it.
 *
 * v2 had no way to delete a model at all: the list row was a single button
 * that opened it, and the store's deleteModel had no caller. A bad import
 * could only be abandoned, not removed. v1 has this on its own selector, but
 * v1 and v2 are separate apps and share stores, not components - so this is
 * v2's, against v2's store, and v1's is untouched.
 *
 * Deleting asks first. The confirm lives in the row rather than in a
 * window.confirm so that the name being deleted stays on screen next to it.
 */
function ModelRow({
  model,
  onOpen,
  folderControl,
  sample = false,
}: {
  model: ModelMetadataV2;
  onOpen: () => void;
  folderControl?: ReactNode;
  /** A sample cannot be deleted - it is reset, or copied (ProjectFolders). */
  sample?: boolean;
}) {
  const deleteModel = useFinancialModelV2Store((s) => s.deleteModel);
  const [confirming, setConfirming] = useState(false);

  // The same row as every discipline's list (ui/ProjectsPage ProjectRow), kept here
  // because this list's tests pin the delete control in this file.
  return (
    <li className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-accent-300">
      <span aria-hidden="true" className="h-9 w-1 shrink-0 rounded-full bg-accent-200 transition-colors group-hover:bg-accent-600" />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left focus-visible:outline-none"
      >
        <span className="block truncate font-display text-[0.95rem] font-semibold text-gray-900 group-hover:text-accent-700">
          {model.name}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">
          Version {model.version} · updated {new Date(model.updatedAt).toLocaleString()}
        </span>
      </button>
      {folderControl}
      {sample ? null : confirming ? (
        <TypedConfirm
          phrase="Delete Project"
          message="This deletes the project and its history permanently."
          action="Delete permanently"
          onConfirm={() => {
            setConfirming(false);
            void deleteModel(model.id);
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          title={`Delete ${model.name}`}
          className="shrink-0 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-red-50 hover:text-red-700"
        >
          Delete
        </button>
      )}
    </li>
  );
}

function ProjectList({
  discipline,
  onLeaveDiscipline,
}: {
  discipline: Discipline;
  onLeaveDiscipline: () => void;
}) {
  const models = useFinancialModelV2Store((s) => s.models);
  const isLoading = useFinancialModelV2Store((s) => s.isLoading);
  const loadError = useFinancialModelV2Store((s) => s.loadError);
  const listModels = useFinancialModelV2Store((s) => s.listModels);
  const loadModel = useFinancialModelV2Store((s) => s.loadModel);
  const samples = useSampleCatalogue('/api/v2/models');
  const createModel = useFinancialModelV2Store((s) => s.createModel);
  const importModel = useFinancialModelV2Store((s) => s.importModel);
  const importPdf = useFinancialModelV2Store((s) => s.importPdf);
  const downloadTemplate = useFinancialModelV2Store((s) => s.downloadTemplate);
  const pdfProgress = useFinancialModelV2Store((s) => s.pdfProgress);
  const pdfError = useFinancialModelV2Store((s) => s.pdfError);
  const pdfWarnings = useFinancialModelV2Store((s) => s.pdfWarnings);
  const dismissPdfReport = useFinancialModelV2Store((s) => s.dismissPdfReport);
  const [name, setName] = useState('');
  const [reread, setReread] = useState(false);

  useEffect(() => {
    listModels();
  }, [listModels]);

  return (
    <ProjectsPage
      discipline={discipline}
      onLeaveDiscipline={onLeaveDiscipline}
      intro="Full statements under IAS and FRS 102: start from the template, or import a filing and check every line it holds."
    >
      {loadError && <Notice>{loadError}</Notice>}

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Panel
          title="Start a model"
          description="An empty model in the template's layout. Download the template to fill it in Excel instead."
        >
          <form
            className="flex gap-2"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!name.trim()) return;
              await createModel(name.trim());
              setName('');
            }}
          >
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="New model name"
              aria-label="New model name"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
            />
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              Create
            </Button>
          </form>
          <Button size="sm" className="mt-3" onClick={() => void downloadTemplate()}>
            Download template
          </Button>
        </Panel>

        <Panel
          title="Import a company's figures"
          description="A filing is read page by page, and every figure it could not vouch for is flagged for you to check."
        >
          <div className="flex flex-wrap gap-2">
            <label
              className={[
                buttonClass('secondary'),
                pdfProgress ? 'cursor-wait opacity-50' : 'cursor-pointer',
              ].join(' ')}
            >
              Import accounts PDF
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                disabled={pdfProgress !== null}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Clear the input so the same file can be retried after a
                  // failure; without this the change event never fires again.
                  e.target.value = '';
                  if (file) void importPdf(file, { reread });
                  setReread(false);
                }}
              />
            </label>
            <ReadAfresh checked={reread} onChange={setReread} disabled={pdfProgress !== null} />
            <label className={`${buttonClass('secondary')} cursor-pointer`}>
              Import workbook
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) importModel(file, file.name.replace(/\.xlsx$/i, ''));
                }}
              />
            </label>
          </div>
          <CompaniesHouseImport />
        </Panel>
      </div>

      {pdfProgress && <PdfImportProgress message={pdfProgress} />}

      {pdfError && <Notice onDismiss={dismissPdfReport}>{pdfError}</Notice>}

      {pdfWarnings.length > 0 && (
        <Notice tone="warning" onDismiss={dismissPdfReport}>
          <div className="font-medium">Imported, with points to check</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {pdfWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Notice>
      )}

      <FolderedProjects
        discipline={discipline.id}
        onCopied={() => void listModels()}
        isLoading={isLoading}
        empty="No models yet. Create one, or import a filing or a workbook in the template's layout."
        samples={samples.map((sample) => ({
          ...sample,
          onOpen: () =>
            void openSampleAt<{ id: string }>('/api/v2/models', sample.key).then(async (model) => {
              await listModels();
              await loadModel(model.id);
            }),
        }))}
        projects={models.map((m) => ({
          id: m.id,
          render: (folderControl, options) => (
            <ModelRow
              key={m.id}
              model={m}
              onOpen={() => loadModel(m.id)}
              folderControl={folderControl}
              sample={options?.sample}
            />
          ),
        }))}
      />
    </ProjectsPage>
  );
}

export function V2App() {
  const model = useFinancialModelV2Store((s) => s.model);
  const openValuation = useValuationStore((s) => s.open);
  const valuation = useValuationStore((s) => s.project);
  const mProject = useMAccountingStore((s) => s.project);
  const diligence = useDiligenceStore((s) => s.project);

  // Chosen before any project list. Remembered, because someone who does
  // accounting all day should not answer the same question at every visit; a
  // stale or absent value simply shows the chooser, so nothing needs migrating.
  const [discipline, setDiscipline] = useState<DisciplineId | null>(readDiscipline);

  const choose = (id: DisciplineId | null) => {
    writeDiscipline(id);
    setDiscipline(id);
  };

  const chosen = disciplineFor(discipline);
  if (!chosen) return <DisciplineChooser onChoose={choose} />;

  // M-accounting has its own projects and workspace, and comes BEFORE the open
  // accounting model below: switching to it must never show an IAS model.
  if (chosen.id === 'm-accounting') {
    if (mProject) return <MWorkspace discipline={chosen} onSwitchDiscipline={choose} />;
    return <MProjectList discipline={chosen} onLeaveDiscipline={() => choose(null)} />;
  }

  // Due diligence, likewise its own projects and workspace, before an open model can take
  // over: its statements are pinned copies, never the open model's live figures.
  if (chosen.id === 'diligence') {
    if (diligence) return <DiligenceWorkspace discipline={chosen} onSwitchDiscipline={choose} />;
    return <DiligenceProjectList discipline={chosen} onLeaveDiscipline={() => choose(null)} />;
  }

  // Books, likewise its own companies and workspace, on Books's API, before an open model can
  // take over: it never shows an IAS model, and reaches the other tools only through a filing.
  if (chosen.id === 'books') {
    return (
      <Suspense fallback={<p className="p-8 text-sm text-gray-600">Opening Books…</p>}>
        <BooksTool discipline={chosen} onSwitchDiscipline={choose} onLeaveDiscipline={() => choose(null)} />
      </Suspense>
    );
  }

  // An open accounting model beats the list, in either discipline: the switch in the
  // workspace header is for looking at THIS model the other way round.
  if (model) return <V2Workspace discipline={chosen} onSwitchDiscipline={choose} />;

  // Valuations are their own projects with their own list, because they can stand on
  // figures with no accounts behind them. See
  // docs/accounting-valuation-separation.md.
  if (chosen.id === 'valuation') {
    // An open valuation gets its own workspace, not the model's: it has six numbers
    // a year and a discount rate, no statements and no balance check, so a
    // sixteen-tab shell would be fifteen empty tabs.
    if (valuation) {
      return (
        <ValuationWorkspace discipline={chosen} onSwitchDiscipline={choose} />
      );
    }
    return (
      <ValuationProjectList
        discipline={chosen}
        onLeaveDiscipline={() => choose(null)}
        onOpen={(id) => void openValuation(id)}
      />
    );
  }

  return <ProjectList discipline={chosen} onLeaveDiscipline={() => choose(null)} />;
}
